import type {
  IRCount,
  IRExpression,
  IRNode,
  IRRow,
  IRSegment,
  IRStep,
  IRInstructionCases,
} from '../../types/ir';
import { exprToInterpolatedText, renderExpressionInline } from '../../logic/text-expression';

export function sanitizeIdent(raw: string): string {
  // Identifiers in the DSL grammar: [a-zA-Z_][a-zA-Z0-9_]*. Strip disallowed
  // characters as the user types so the saved DSL stays parseable.
  const cleaned = raw.replace(/[^a-zA-Z0-9_]/g, '_');
  if (!cleaned) return '_';
  if (/^[0-9]/.test(cleaned)) return '_' + cleaned;
  return cleaned;
}

export const isRuleBlockGroup = (node: IRNode): boolean =>
  node.kind === 'group' &&
  node.body.length === 1 &&
  node.body[0].kind === 'row' &&
  node.body[0].instruction.kind === 'instruction_cases';

export const createDefaultNode = (
  kind: 'segment' | 'row' | 'group' | 'note' | 'rule_block',
): IRSegment | IRStep => {
  const id = crypto.randomUUID();
  switch (kind) {
    case 'segment':
      return { id, kind: 'segment', name: '', counters: [], setup: [], body: [] };
    case 'row':
      return {
        id,
        kind: 'row',
        instruction: {
          kind: 'instruction_text',
          expr: { kind: 'literal', type: 'text', value: '' },
        },
        deltas: [],
      };
    case 'note':
      return { id, kind: 'note', text_expr: { kind: 'literal', type: 'text', value: '' } };
    case 'group':
      return {
        id,
        kind: 'group',
        iter_name: null,
        count: { kind: 'count_fixed', expr: { kind: 'literal', type: 'int', value: 1 } },
        counters: [],
        body: [],
      };
    case 'rule_block':
      return {
        id,
        kind: 'group',
        // 'row' is a reserved DSL keyword and cannot be used as an iter name;
        // 'r' matches the convention in examples/sock.knit.
        iter_name: 'r',
        count: { kind: 'count_fixed', expr: { kind: 'literal', type: 'int', value: 1 } },
        counters: [],
        body: [
          {
            id: crypto.randomUUID(),
            kind: 'row',
            instruction: {
              kind: 'instruction_cases',
              cases: [
                {
                  when: {
                    kind: 'binop',
                    op: '==',
                    left: {
                      kind: 'binop',
                      op: '%',
                      left: { kind: 'counter_ref', name: 'r' },
                      right: { kind: 'literal', type: 'int', value: 1 },
                    },
                    right: { kind: 'literal', type: 'int', value: 0 },
                  },
                  row: {
                    id: crypto.randomUUID(),
                    kind: 'row',
                    instruction: {
                      kind: 'instruction_text',
                      expr: { kind: 'literal', type: 'text', value: '' },
                    },
                    deltas: [],
                  },
                },
              ],
              else: null,
            },
            deltas: [],
          },
        ],
      };
  }
};

// `title` is the single bold header line (segment name, "Repeat 3×", row text).
// `body` is a recursive multi-line summary of the children, indented two spaces
// per nesting level. The collapsed view renders `body` as plain pre-wrapped
// text below the title so the structure of the contents is visible without
// any of the editing controls.
export interface NodeDescription {
  title: string;
  body: string;
}

const MAX_DEPTH = 6;
const INDENT = '  ';

const rowText = (row: IRRow): string => {
  if (row.instruction?.kind === 'instruction_text') {
    return exprToInterpolatedText(row.instruction.expr);
  }
  return '';
};

const noteText = (note: { text_expr: IRExpression }): string =>
  exprToInterpolatedText(note.text_expr);

const renderCountExpr = (expr: IRExpression): string => {
  if (expr.kind === 'literal' && expr.type === 'text') return (expr.value as string) || '…';
  return renderExpressionInline(expr);
};

const countSummary = (count: IRCount): string => {
  switch (count.kind) {
    case 'count_fixed':
      return `${renderCountExpr(count.expr)}×`;
    case 'count_until_text':
      return `until ${renderCountExpr(count.text_expr)}`;
    case 'count_until_pred':
      return `until ${renderCountExpr(count.predicate)}`;
  }
};

const summarizeRuleBlockCases = (instruction: IRInstructionCases, depth: number): string => {
  const indent = INDENT.repeat(depth);
  const lines: string[] = [];
  for (const c of instruction.cases) {
    lines.push(`${indent}if ${renderExpressionInline(c.when)}: ${rowText(c.row) || '…'}`);
  }
  if (instruction.else) {
    lines.push(`${indent}else: ${rowText(instruction.else) || '…'}`);
  }
  return lines.join('\n');
};

export const summarizeChildLine = (node: IRSegment | IRStep, depth: number): string => {
  if (depth > MAX_DEPTH) return `${INDENT.repeat(depth)}…`;
  const indent = INDENT.repeat(depth);
  switch (node.kind) {
    case 'segment': {
      const name = node.name || 'Untitled';
      const inner = summarizeChildren(node.body, depth + 1);
      return inner ? `${indent}${name}:\n${inner}` : `${indent}${name}`;
    }
    case 'row':
      return `${indent}${rowText(node) || '(empty row)'}`;
    case 'note':
      return `${indent}note: ${noteText(node) || '(empty note)'}`;
    case 'group': {
      if (isRuleBlockGroup(node)) {
        const firstRow = node.body[0] as IRRow;
        const header = `${countSummary(node.count)} rules:`;
        const cases = summarizeRuleBlockCases(
          firstRow.instruction as IRInstructionCases,
          depth + 1,
        );
        return cases ? `${indent}${header}\n${cases}` : `${indent}${header}`;
      }
      const header = `${countSummary(node.count)}:`;
      const inner = summarizeChildren(node.body, depth + 1);
      return inner ? `${indent}${header}\n${inner}` : `${indent}${header}`;
    }
  }
  return '';
};

const summarizeChildren = (children: (IRSegment | IRStep)[], depth: number): string =>
  children
    .map((c) => summarizeChildLine(c, depth))
    .filter(Boolean)
    .join('\n');

export const describeNode = (node: IRNode): NodeDescription => {
  switch (node.kind) {
    case 'pattern':
      return {
        title: node.name || 'Untitled',
        body: summarizeChildren(node.segments, 0),
      };
    case 'segment':
      return {
        title: node.name || 'Untitled',
        body: summarizeChildren(node.body, 0),
      };
    case 'row':
      return {
        title: rowText(node) || '(empty row)',
        body: '',
      };
    case 'note':
      return {
        title: noteText(node) || '(empty note)',
        body: '',
      };
    case 'group':
      if (isRuleBlockGroup(node)) {
        const firstRow = node.body[0] as IRRow;
        return {
          title: `Rule Block · ${countSummary(node.count)}`,
          body: summarizeRuleBlockCases(firstRow.instruction as IRInstructionCases, 0),
        };
      }
      return {
        title: `Repeat ${countSummary(node.count)}`,
        body: summarizeChildren(node.body, 0),
      };
    default:
      return { title: '', body: '' };
  }
};

export const WELL_KNOWN_METADATA_KEYS = [
  'project_type',
  'yarn_weight',
  'gauge',
  'needle_size',
] as const;

export const PROJECT_TYPE_OPTIONS = [
  'sock',
  'hat',
  'sweater',
  'scarf',
  'cowl',
  'mittens',
  'shawl',
  'blanket',
  'vest',
  'tee',
] as const;

export type ProjectTypeOption = (typeof PROJECT_TYPE_OPTIONS)[number];

export const PROJECT_TYPE_SYNONYMS: Record<string, ProjectTypeOption> = {
  socks: 'sock',
  beanie: 'hat',
  pullover: 'sweater',
  cardigan: 'sweater',
  gloves: 'mittens',
  wrap: 'shawl',
  afghan: 'blanket',
};

/**
 * Resolve a metadata.project_type value to one of the canonical options, or
 * `null` if it is missing or unrecognized (caller renders the default glyph).
 */
export function resolveProjectType(
  metadata: Record<string, string> | undefined,
): ProjectTypeOption | null {
  const raw = metadata?.project_type;
  if (typeof raw !== 'string') return null;
  const normalized = raw.trim().toLowerCase();
  if (!normalized) return null;
  if ((PROJECT_TYPE_OPTIONS as readonly string[]).includes(normalized)) {
    return normalized as ProjectTypeOption;
  }
  if (normalized in PROJECT_TYPE_SYNONYMS) {
    return PROJECT_TYPE_SYNONYMS[normalized];
  }
  return null;
}
